/**
 * Outil « Repère de l'espace » (Boîte à outils, Tle → prépa).
 *
 * Une figure 3D qu'on fait tourner (glisser, flèches, boutons). Points :
 * A et B à coordonnées entières, pointillés jusqu'aux plans de coordonnées,
 * vecteur AB, longueur exacte, milieu. Vecteurs : u, v, w ; v = k u ?
 * w = a u + b v (système résolu sur deux coordonnées, vérifié sur la
 * troisième) : coplanaires ou base de l'espace. Droite et plan : plan de
 * vecteur normal n passant par A (ax + by + cz + d = 0), droite 𝒟
 * (B, u) paramétrée, position relative et point d'intersection I, projeté
 * orthogonal H de B et distance au plan (méthode de Terminale |t| × ‖n‖ ;
 * la formule |ax₀ + by₀ + cz₀ + d| / ‖n‖ en prépa). Défi en 5 questions.
 * Logique : `space.ts`.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { cn } from '@jeraisonne/ui';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { Note, PRIMARY_BUTTON, SECONDARY_BUTTON, Segmented, ToggleChip } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { seededRng } from '../../ui/random';
import { add, normalize, scale, type V3 } from '../../ui/space3d';
import { FIGURE_BG_CLASS, HaloText, MUTED, VectorArrow } from '../../ui/svg-parts';
import { ORBIT_FOCUS_CLASS, OrbitButtons, pointsAttr, projector, useOrbit } from '../../ui/view3d';
import { frac, value, type Frac } from '../../ui/exact';
import {
  COORD_MAX,
  COORD_MIN,
  DEFAULTS,
  DEFAULT_VIEW,
  QUIZ_KINDS,
  affineText,
  clipLine,
  colinearity,
  comboText,
  coplanarity,
  fracText,
  icross,
  idot,
  inCube,
  isNull,
  lengthText,
  lineProjection,
  makeQuiz,
  n2,
  paramLines,
  planeD,
  planePatch,
  planeText,
  points,
  position,
  projection,
  rootText,
  vecText,
  type IV,
  type QV,
  type Quiz,
} from './space';

const SIZE = 320;
/** cube dessiné */
const R = 5;
/** la demi-diagonale du cube des saisies [−4 ; 4]³ tient dans la figure */
const PX = (SIZE / 2 - 22) / (4 * Math.sqrt(3) + 0.6);
const SELECT =
  'border-ink-200 text-ink-900 min-h-11 rounded-xl border bg-white px-1 text-center text-[15px] tabular-nums';
const VALUES = Array.from({ length: COORD_MAX - COORD_MIN + 1 }, (_, i) => COORD_MIN + i);
/** couleurs lisibles en texte dans les deux thèmes (primary-600 : bleu clair en sombre) */
const C = {
  a: 'var(--color-primary-600)',
  b: 'var(--color-accent-600)',
  c: 'var(--color-success-600)',
  plane: 'var(--color-primary-500)',
  ink: 'var(--color-ink-700)',
};
const NB = String.fromCharCode(0xa0);
const NNB = String.fromCharCode(0x202f);
const qnum = (v: QV): V3 => [value(v[0]), value(v[1]), value(v[2])];
const num = (v: IV): V3 => [v[0], v[1], v[2]];
const mm = (v: number) => String(v).replace('-', '−');
/** coordonnées insécables, comme `vecText` */
const tuple = (parts: string[]) => `(${parts.join(`${NNB};${NB}`)})`;
const far = (p: V3) => (inCube(p) ? '' : ' (hors de la figure)');

/* ---------- saisie ---------- */

function CoordRow({
  name,
  label,
  value: v,
  onChange,
  color,
}: {
  name: string;
  /** nom pour le lecteur d'écran : « Point A », « Vecteur normal n » */
  label: string;
  value: IV;
  onChange: (v: IV) => void;
  color: string;
}) {
  const coords = ['x', 'y', 'z'];
  return (
    <div role="group" aria-label={label} className="flex items-center gap-1.5">
      <span className="w-10 shrink-0 text-base font-bold" style={{ color }}>
        {name}
      </span>
      <span aria-hidden className="text-ink-500 text-lg">
        (
      </span>
      {v.map((c, i) => (
        <span key={i} className="flex items-center gap-1.5">
          <select
            className={cn(SELECT, 'w-14')}
            aria-label={`${label} : ${coords[i]}`}
            value={c}
            onChange={(e) => {
              const w = [...v] as [number, number, number];
              w[i] = Number(e.target.value);
              onChange(w);
            }}
          >
            {VALUES.map((n) => (
              <option key={n} value={n}>
                {mm(n)}
              </option>
            ))}
          </select>
          {i < 2 && (
            <span aria-hidden className="text-ink-500">
              ;
            </span>
          )}
        </span>
      ))}
      <span aria-hidden className="text-ink-500 text-lg">
        )
      </span>
    </div>
  );
}

/* ---------- figure ---------- */

type Proj = (p: V3) => [number, number];
const clampX = (x: number) => Math.min(SIZE - 12, Math.max(12, x));
const clampY = (y: number) => Math.min(SIZE - 6, Math.max(14, y));

function Axes({ P }: { P: Proj }) {
  const L = R * 1.05;
  const axes: [V3, string][] = [
    [[1, 0, 0], 'x'],
    [[0, 1, 0], 'y'],
    [[0, 0, 1], 'z'],
  ];
  return (
    <g aria-hidden>
      {axes.map(([a, name]) => {
        const [x1, y1] = P(scale(a, -L));
        const [x2, y2] = P(scale(a, L));
        const [lx, ly] = P(scale(a, L * 1.09));
        return (
          <g key={name}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={MUTED} strokeWidth={1} />
            {/* graduations de −4 à 4 */}
            {[-4, -3, -2, -1, 1, 2, 3, 4].map((k) => {
              const [tx, ty] = P(scale(a, k));
              return <circle key={k} cx={tx} cy={ty} r={k > 0 ? 1.8 : 1.2} fill={MUTED} />;
            })}
            <text
              x={clampX(lx)}
              y={clampY(ly + 4)}
              fontSize={13}
              fill="var(--color-ink-500)"
              textAnchor="middle"
            >
              {name}
            </text>
          </g>
        );
      })}
      <text
        x={P([0, 0, 0])[0] - 10}
        y={P([0, 0, 0])[1] + 14}
        fontSize={12}
        fill="var(--color-ink-500)"
      >
        O
      </text>
    </g>
  );
}

/** pointillés de M jusqu'au plan (xOy) puis aux axes : on lit les coordonnées */
function Box({ P, M, color }: { P: Proj; M: V3; color: string }) {
  const [x, y, z] = M;
  const foot: V3 = [x, y, 0];
  const segs: [V3, V3][] = [];
  if (z !== 0) segs.push([M, foot], [M, [0, 0, z]]);
  // un segment porté par un axe doublerait l'axe : on l'omet
  if (y !== 0 && x !== 0) segs.push([foot, [x, 0, 0]], [foot, [0, y, 0]]);
  return (
    <g aria-hidden>
      {segs.map(([p, q], i) => {
        const [x1, y1] = P(p);
        const [x2, y2] = P(q);
        return (
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={color}
            strokeOpacity={0.6}
            strokeWidth={1.25}
            strokeDasharray="4 4"
          />
        );
      })}
    </g>
  );
}

interface DotSpec {
  M: V3;
  label: string;
  color: string;
}

/** points nommés ; un nom trop près d'un autre passe dessous */
function Dots({ P, dots }: { P: Proj; dots: DotSpec[] }) {
  const placed: { x: number; y: number }[] = [];
  return (
    <g aria-hidden>
      {dots.map((d) => {
        if (!inCube(d.M, R + 1)) return null;
        const [x, y] = P(d.M);
        let lx = clampX(x + 9);
        let ly = clampY(y - 8);
        if (placed.some((q) => Math.abs(q.x - lx) < 16 && Math.abs(q.y - ly) < 14)) {
          lx = clampX(x + 9);
          ly = clampY(y + 18);
        }
        placed.push({ x: lx, y: ly });
        return (
          <g key={d.label}>
            <circle cx={x} cy={y} r={5} fill={d.color} />
            <HaloText x={lx} y={ly} fontSize={14} fontWeight={700} fill={d.color}>
              {d.label}
            </HaloText>
          </g>
        );
      })}
    </g>
  );
}

function Arrow({
  P,
  from,
  to,
  color,
  label,
}: {
  P: Proj;
  from: V3;
  to: V3;
  color: string;
  label?: string;
}) {
  const [x1, y1] = P(from);
  const [x2, y2] = P(to);
  const d = Math.hypot(x2 - x1, y2 - y1);
  return (
    <g aria-hidden>
      <VectorArrow x1={x1} y1={y1} x2={x2} y2={y2} color={color} width={3} />
      {label && d > 1 && (
        <HaloText
          x={clampX(x2 + ((x2 - x1) / d) * 14)}
          y={clampY(y2 + ((y2 - y1) / d) * 14 + 4)}
          fontSize={14}
          fontWeight={700}
          fill={color}
          textAnchor="middle"
        >
          {label}
        </HaloText>
      )}
    </g>
  );
}

function PlanePatch({ P, patch }: { P: Proj; patch: ReturnType<typeof planePatch> }) {
  const { center, e1, e2, half } = patch;
  const corners = [
    [1, 1],
    [-1, 1],
    [-1, -1],
    [1, -1],
  ].map(([s, t]) =>
    P([
      center[0] + (s! * e1[0] + t! * e2[0]) * half,
      center[1] + (s! * e1[1] + t! * e2[1]) * half,
      center[2] + (s! * e1[2] + t! * e2[2]) * half,
    ]),
  );
  return (
    <polygon
      aria-hidden
      points={pointsAttr(corners)}
      fill={C.plane}
      fillOpacity={0.14}
      stroke={C.plane}
      strokeOpacity={0.55}
      strokeWidth={1}
    />
  );
}

function Segment({
  P,
  p,
  q,
  color,
  dashed,
}: {
  P: Proj;
  p: V3;
  q: V3;
  color: string;
  dashed?: boolean;
}) {
  const [x1, y1] = P(p);
  const [x2, y2] = P(q);
  return (
    <line
      aria-hidden
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      stroke={color}
      strokeWidth={dashed ? 2 : 2.5}
      strokeDasharray={dashed ? '5 4' : undefined}
    />
  );
}

function Figure({
  label,
  orbit,
  children,
}: {
  label: string;
  orbit: ReturnType<typeof useOrbit>;
  children: (P: Proj) => ReactNode;
}) {
  const P = projector(orbit.cam, [0, 0, 0], PX, SIZE);
  return (
    <div className="space-y-2">
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={frenchSpaces(label)}
        className={cn(
          'border-ink-100 mx-auto block w-full max-w-[360px] cursor-grab select-none rounded-2xl border',
          FIGURE_BG_CLASS,
          ORBIT_FOCUS_CLASS,
        )}
        {...orbit.svgProps}
      >
        <Axes P={P} />
        {children(P)}
      </svg>
      <OrbitButtons orbit={orbit} />
    </div>
  );
}

function Lines({ lines, id, live }: { lines: string[]; id: string; live: string }) {
  return (
    <>
      <p className="sr-only" aria-live="polite">
        {frenchSpaces(live)}
      </p>
      <Note live={false}>
        {lines.map((l) => (
          <span key={l} data-testid={id} className="mb-1 block last:mb-0">
            {frenchSpaces(l)}
          </span>
        ))}
      </Note>
    </>
  );
}

/* ---------- points ---------- */

function Points() {
  const [A, setA] = useState<IV>(DEFAULTS.points.A);
  const [B, setB] = useState<IV>(DEFAULTS.points.B);
  const [box, setBox] = useState(true);
  const orbit = useOrbit(DEFAULT_VIEW);
  const info = points(A, B);
  const diff = (b: number, a: number) => `${mm(b)} − ${a < 0 ? `(${mm(a)})` : a}`;
  const lines = [
    `Vecteur AB${tuple([diff(B[0], A[0]), diff(B[1], A[1]), diff(B[2], A[2])])} = ${vecText(info.AB)}.`,
    `${lengthText(info.AB)} (repère orthonormé).`,
    `Milieu de [AB] : I${vecText(info.middle)}.`,
  ];
  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <CoordRow name="A" label="Point A" value={A} onChange={setA} color={C.a} />
        <CoordRow name="B" label="Point B" value={B} onChange={setB} color={C.b} />
      </div>
      <ToggleChip on={box} onClick={() => setBox(!box)}>
        Lire les coordonnées (pointillés)
      </ToggleChip>
      <Figure
        orbit={orbit}
        label={`Repère de l’espace : A${vecText(A)}, B${vecText(B)}, vecteur AB${vecText(info.AB)}.`}
      >
        {(P) => (
          <>
            {box && <Box P={P} M={num(A)} color={C.a} />}
            {box && <Box P={P} M={num(B)} color={C.b} />}
            {!isNull(info.AB) && <Arrow P={P} from={num(A)} to={num(B)} color={C.ink} />}
            <Dots
              P={P}
              dots={[
                { M: num(A), label: 'A', color: C.a },
                { M: num(B), label: 'B', color: C.b },
                { M: qnum(info.middle), label: 'I', color: C.c },
              ]}
            />
          </>
        )}
      </Figure>
      <Lines
        lines={lines}
        id="sf-points"
        live={`AB = ${info.length}, milieu I${vecText(info.middle)}`}
      />
    </div>
  );
}

/* ---------- vecteurs ---------- */

const COORD = ['x', 'y', 'z'];
/** « a + b », « 2a − b », « −a », « 0 » */
function lin(p: number, q: number) {
  const t = (k: number, v: string, first: boolean) => {
    if (k === 0) return '';
    const abs = Math.abs(k) === 1 ? v : `${Math.abs(k)}${v}`;
    return first ? `${k < 0 ? '−' : ''}${abs}` : ` ${k < 0 ? '−' : '+'} ${abs}`;
  };
  return `${t(p, 'a', true)}${t(q, 'b', p === 0)}` || '0';
}

function Vectors() {
  const [u, setU] = useState<IV>(DEFAULTS.vectors.u);
  const [v, setV] = useState<IV>(DEFAULTS.vectors.v);
  const [w, setW] = useState<IV>(DEFAULTS.vectors.w);
  const orbit = useOrbit(DEFAULT_VIEW);
  const col = colinearity(u, v);
  const cop = coplanarity(u, v, w);
  const lines: string[] = [
    col.kind === 'null'
      ? `${col.which} est le vecteur nul : il est colinéaire à tout vecteur.`
      : col.kind === 'yes'
        ? `u et v sont colinéaires : v = ${comboText(col.k, frac(0))} (les trois coordonnées sont proportionnelles).`
        : `u et v ne sont pas colinéaires : ${col.why}.`,
  ];
  let live = '';
  if (cop.kind === 'colinear') {
    lines.push(
      'u et v sont colinéaires : u, v, w sont donc coplanaires (la méthode w = a u + b v suppose u et v non colinéaires).',
    );
    live = 'u et v colinéaires : u, v, w coplanaires';
  } else {
    const [i, j] = cop.rows;
    const k = 3 - i - j;
    lines.push(
      `On cherche a et b avec w = a u + b v. Coordonnées ${COORD[i]} et ${COORD[j]} : ${lin(u[i]!, v[i]!)} = ${mm(w[i]!)} et ${lin(u[j]!, v[j]!)} = ${mm(w[j]!)}, d’où a = ${fracText(cop.a)} et b = ${fracText(cop.b)}.`,
    );
    const got: Frac = cop.kind === 'yes' ? frac(w[k]!) : cop.got;
    lines.push(
      cop.kind === 'yes'
        ? `Coordonnée ${COORD[k]} : ${lin(u[k]!, v[k]!)} vaut ${fracText(got)} pour ces valeurs, et w a bien ${COORD[k]} = ${mm(w[k]!)} ✓. Donc w = ${comboText(cop.a, cop.b)} : u, v, w sont coplanaires.`
        : `Coordonnée ${COORD[k]} : ${lin(u[k]!, v[k]!)} vaut ${fracText(got)} pour ces valeurs, mais w a ${COORD[k]} = ${mm(w[k]!)}. Aucun couple (a ; b) ne convient : u, v, w ne sont pas coplanaires, (u, v, w) est une base de l’espace.`,
    );
    live =
      cop.kind === 'yes'
        ? `w = ${comboText(cop.a, cop.b)} : u, v, w coplanaires`
        : 'u, v, w non coplanaires : base de l’espace';
  }
  const span = cop.kind !== 'colinear';
  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <CoordRow name="u" label="Vecteur u" value={u} onChange={setU} color={C.a} />
        <CoordRow name="v" label="Vecteur v" value={v} onChange={setV} color={C.b} />
        <CoordRow name="w" label="Vecteur w" value={w} onChange={setW} color={C.c} />
      </div>
      <Figure
        orbit={orbit}
        label={`Vecteurs u${vecText(u)}, v${vecText(v)}, w${vecText(w)} depuis O${span ? ', avec le plan engendré par u et v' : ''}.`}
      >
        {(P) => (
          <>
            {span && (
              <PlanePatch
                P={P}
                patch={planePatch(
                  num(icross(u, v)),
                  [0, 0, 0],
                  [num(u), num(v), ...(cop.kind === 'yes' ? [num(w)] : [])],
                )}
              />
            )}
            <Arrow P={P} from={[0, 0, 0]} to={num(u)} color={C.a} label="u" />
            <Arrow P={P} from={[0, 0, 0]} to={num(v)} color={C.b} label="v" />
            <Arrow P={P} from={[0, 0, 0]} to={num(w)} color={C.c} label="w" />
          </>
        )}
      </Figure>
      <Lines lines={lines} id="sf-vectors" live={live} />
      <p className="text-ink-500 text-xs">
        {frenchSpaces(
          'En bleu clair, le plan passant par O dirigé par u et v (quand ils ne sont pas colinéaires) : w est coplanaire avec u et v exactement quand sa flèche reste dans ce plan. Fais tourner la figure pour le voir.',
        )}
      </p>
    </div>
  );
}

/* ---------- droite et plan ---------- */

function LinePlane({ prepa }: { prepa: boolean }) {
  const [n, setN] = useState<IV>(DEFAULTS.plane.n);
  const [A, setA] = useState<IV>(DEFAULTS.plane.A);
  const [B, setB] = useState<IV>(DEFAULTS.plane.B);
  const [u, setU] = useState<IV>(DEFAULTS.plane.u);
  const [proj, setProj] = useState(false);
  const [M, setM] = useState<IV>(DEFAULTS.plane.M);
  const [projLine, setProjLine] = useState(false);
  const orbit = useOrbit(DEFAULT_VIEW);
  const okN = !isNull(n);
  const okU = !isNull(u);
  const Pl = { n, A };
  const Ln = { B, u };
  const d = planeD(Pl);
  const pos = okN && okU ? position(Pl, Ln) : null;
  const pr = okN ? projection(Pl, B) : null;
  const lp = okU ? lineProjection(Ln, M) : null;
  const lines: string[] = [];
  if (!okN) lines.push('Le vecteur normal n ne peut pas être nul : choisis-en un autre.');
  else
    lines.push(
      `n${vecText(n)} est normal à 𝒫, donc 𝒫 : ${planeText({ n, A: [0, 0, 0] }).replace(' = 0', '')} + d = 0 ; A ∈ 𝒫 donne ${idot(n, A) === 0 ? 'd = 0, et' : `${affineText(1, idot(n, A), 'd', true)} = 0, d’où d = ${mm(d)} et`} 𝒫 : ${planeText(Pl)}.`,
    );
  if (!okU) lines.push('Le vecteur directeur u ne peut pas être nul : choisis-en un autre.');
  else lines.push(`𝒟 : ${paramLines(Ln).join(', ')} (t ∈ ℝ).`);
  if (pos) {
    const nu = idot(n, u);
    const val = idot(n, B) + d;
    lines.push(
      pos.kind === 'secante'
        ? `n · u = ${mm(nu)} ≠ 0 : 𝒟 coupe 𝒫 en un point. En remplaçant x, y, z dans l’équation de 𝒫 : ${affineText(nu, val, 't')} = 0, donc t = ${fracText(pos.t)}, d’où I${vecText(pos.H)}${far(qnum(pos.H))}.${pos.orth ? ' De plus u est colinéaire à n : 𝒟 est orthogonale à 𝒫.' : ''}`
        : pos.kind === 'incluse'
          ? 'n · u = 0 et B ∈ 𝒫 : la droite 𝒟 est incluse dans 𝒫.'
          : 'n · u = 0 et B ∉ 𝒫 : la droite 𝒟 est strictement parallèle à 𝒫.',
    );
  }
  if (proj && pr) {
    const nn = n2(n);
    const val = idot(n, B) + d;
    lines.push(
      pr.num === 0
        ? 'B est sur 𝒫 : il est son propre projeté, et la distance de B à 𝒫 est nulle.'
        : `Projeté orthogonal de B sur 𝒫 : sur la droite passant par B dirigée par n (${paramLines({ B, u: n }, 's').join(', ')}), l’équation de 𝒫 donne ${affineText(nn, val, 's')} = 0, donc s = ${fracText(pr.t)}, d’où H${vecText(pr.H)}${far(qnum(pr.H))}. Distance : BH = |s| × ‖n‖ = ${fracText(frac(pr.num, nn))} × ${rootText(nn)} = ${pr.dist}.`,
    );
    if (prepa && pr.num !== 0)
      lines.push(
        `Formule : d(B, 𝒫) = |a x₀ + b y₀ + c z₀ + d| / √(a² + b² + c²) = |${mm(val)}| / √${nn} = ${pr.dist}.`,
      );
  }
  if (projLine && lp) {
    const uu = n2(u);
    lines.push(
      isNull(lp.cross)
        ? 'M est sur 𝒟 : il est son propre projeté, et la distance de M à 𝒟 est nulle.'
        : `Projeté orthogonal de M sur 𝒟 : K${tuple(paramLines(Ln).map((e) => e.split(' = ')[1]!))} est sur 𝒟 et MK · u = 0, soit ${affineText(uu, -idot(lp.BM, u), 't')} = 0 ; donc t = ${fracText(lp.t)}, d’où K${vecText(lp.K)}${far(qnum(lp.K))}. Distance : MK${vecText(lp.MK)}, MK = ${lp.dist}.`,
    );
    if (prepa && !isNull(lp.cross))
      lines.push(
        `Formule : d(M, 𝒟) = ‖BM ∧ u‖ / ‖u‖ avec BM${vecText(lp.BM)} et BM ∧ u = ${vecText(lp.cross)} : d(M, 𝒟) = √${n2(lp.cross)} / √${uu} = ${lp.dist}.`,
      );
  }
  const live = !okN
    ? 'n nul'
    : `𝒫 : ${planeText(Pl)}${pos ? (pos.kind === 'secante' ? ` ; 𝒟 coupe 𝒫 en I${vecText(pos.H)}` : pos.kind === 'incluse' ? ' ; 𝒟 incluse dans 𝒫' : ' ; 𝒟 parallèle à 𝒫') : ''}${proj && pr ? ` ; distance de B à 𝒫 : ${pr.dist}` : ''}${projLine && lp ? ` ; distance de M à 𝒟 : ${lp.dist}` : ''}`;
  const label = `${okN ? `Plan 𝒫 : ${planeText(Pl)}` : 'Plan non défini (n nul)'} et droite 𝒟 passant par B${vecText(B)}${okU ? ` dirigée par u${vecText(u)}` : ''}.`;
  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <p className="text-ink-700 text-sm font-semibold">Plan 𝒫</p>
        <CoordRow name="n" label="Vecteur normal n" value={n} onChange={setN} color={C.a} />
        <CoordRow name="A" label="Point A du plan" value={A} onChange={setA} color={C.a} />
        <p className="text-ink-700 pt-1 text-sm font-semibold">Droite 𝒟</p>
        <CoordRow name="B" label="Point B de la droite" value={B} onChange={setB} color={C.b} />
        <CoordRow name="u" label="Vecteur directeur u" value={u} onChange={setU} color={C.b} />
      </div>
      <div className="flex flex-wrap gap-2">
        <ToggleChip on={proj} onClick={() => setProj(!proj)}>
          Projeté de B sur 𝒫 et distance
        </ToggleChip>
        <ToggleChip on={projLine} onClick={() => setProjLine(!projLine)}>
          Projeté d’un point M sur 𝒟
        </ToggleChip>
      </div>
      {projLine && (
        <CoordRow name="M" label="Point M à projeter sur 𝒟" value={M} onChange={setM} color={C.c} />
      )}
      <Figure orbit={orbit} label={label}>
        {(P) => {
          const seg = okU ? clipLine(num(B), num(u), R) : null;
          const line = seg && (
            <Segment
              P={P}
              p={add(num(B), scale(num(u), seg[0]))}
              q={add(num(B), scale(num(u), seg[1]))}
              color={C.b}
            />
          );
          // projeté de M sur 𝒟 (indépendant du plan)
          const onLine =
            projLine && lp ? (
              <>
                {!isNull(lp.cross) && (
                  <Segment P={P} p={num(M)} q={qnum(lp.K)} color={C.c} dashed />
                )}
              </>
            ) : null;
          const lineDots =
            projLine && lp
              ? [
                  { M: num(M), label: 'M', color: C.c },
                  ...(isNull(lp.cross) ? [] : [{ M: qnum(lp.K), label: 'K', color: C.c }]),
                ]
              : [];
          if (!okN)
            return (
              <>
                {line}
                {onLine}
                <Dots P={P} dots={[{ M: num(B), label: 'B', color: C.b }, ...lineDots]} />
              </>
            );
          const nv = num(n);
          const c0 = scale(nv, -d / n2(n));
          // le carré du plan contient A, H et I (s'ils sont dans le cube)
          const inside = [num(A)];
          if (pos?.kind === 'secante' && inCube(qnum(pos.H))) inside.push(qnum(pos.H));
          if (proj && pr && inCube(qnum(pr.H))) inside.push(qnum(pr.H));
          return (
            <>
              <PlanePatch P={P} patch={planePatch(nv, c0, inside)} />
              {line}
              <Arrow
                P={P}
                from={num(A)}
                to={add(num(A), scale(normalize(nv), 1.6))}
                color={C.a}
                label="n"
              />
              {okU && <Arrow P={P} from={num(B)} to={add(num(B), num(u))} color={C.b} label="u" />}
              {proj && pr && pr.num !== 0 && (
                <Segment P={P} p={num(B)} q={qnum(pr.H)} color={C.c} dashed />
              )}
              {onLine}
              <Dots
                P={P}
                dots={[
                  { M: num(A), label: 'A', color: C.a },
                  { M: num(B), label: 'B', color: C.b },
                  ...(pos?.kind === 'secante'
                    ? [{ M: qnum(pos.H), label: 'I', color: C.ink }]
                    : []),
                  ...(proj && pr && pr.num !== 0
                    ? [{ M: qnum(pr.H), label: 'H', color: C.c }]
                    : []),
                  ...lineDots,
                ]}
              />
            </>
          );
        }}
      </Figure>
      <Lines lines={lines} id="sf-plane" live={live} />
      <p className="text-ink-500 text-xs">
        {frenchSpaces(
          'Les coefficients de x, y, z dans l’équation sont les coordonnées du vecteur normal. La droite coupe le plan quand n · u ≠ 0 ; sinon elle lui est parallèle (ou incluse).',
        )}
      </p>
    </div>
  );
}

/* ---------- défi ---------- */

function Challenge({ seed, prepa }: { seed?: number; prepa: boolean }) {
  const rnd = useMemo(() => (seed === undefined ? Math.random : seededRng(seed)), [seed]);
  const rounds = QUIZ_KINDS.length;
  const [count, setCount] = useState(1);
  const [q, setQ] = useState<Quiz>(() => makeQuiz(QUIZ_KINDS[0]!, rnd, prepa));
  const [picked, setPicked] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const moved = useRef(false);
  const done = picked !== null && count >= rounds;
  const chosen = picked === null ? null : q.options[picked]!;
  const right = q.options.find((o) => o.correct)!;

  useEffect(() => {
    if (moved.current) titleRef.current?.focus();
    moved.current = false;
  }, [q]);

  const choose = (i: number) => {
    if (picked !== null) return;
    setPicked(i);
    if (q.options[i]!.correct) setScore(score + 1);
  };
  const next = () => {
    const c = done ? 1 : count + 1;
    if (done) setScore(0);
    setCount(c);
    moved.current = true;
    setQ(makeQuiz(QUIZ_KINDS[c - 1]!, rnd, prepa));
    setPicked(null);
  };

  return (
    <div className="space-y-3">
      <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
        {frenchSpaces(`Question ${count} / ${rounds} · ${score} juste${score > 1 ? 's' : ''}`)}
      </p>
      <p ref={titleRef} tabIndex={-1} className="text-ink-900 text-base font-semibold outline-none">
        {frenchSpaces(q.prompt)}
      </p>
      <div role="group" aria-label="Réponses" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {q.options.map((o, i) => (
          <button
            key={o.text}
            type="button"
            onClick={() => choose(i)}
            aria-pressed={picked === i}
            aria-disabled={picked !== null && picked !== i}
            className={cn(
              'min-h-11 rounded-xl border px-3 py-2 text-center text-base font-semibold tabular-nums transition-colors',
              picked === null && 'border-ink-200 text-ink-800 hover:bg-ink-50',
              picked !== null && o.correct && 'border-success-600 bg-success-50 text-ink-900',
              picked === i && !o.correct && 'border-error-500 bg-error-50 text-ink-900',
              picked !== null && picked !== i && !o.correct && 'border-ink-100 text-ink-500',
            )}
          >
            {frenchSpaces(o.text)}
          </button>
        ))}
      </div>
      <div aria-live="polite">
        {chosen && (
          <Note live={false} tone={chosen.correct ? 'success' : 'error'}>
            <span data-testid="sf-feedback">
              {frenchSpaces(
                chosen.correct
                  ? `Exact. ${q.explain}`
                  : `${chosen.hint ?? ''} La bonne réponse : ${right.text}. ${q.explain}`,
              )}
            </span>
          </Note>
        )}
      </div>
      {picked !== null && (
        <div className="flex flex-wrap items-center gap-3">
          {done && (
            <p className="text-ink-900 text-sm font-semibold">
              {frenchSpaces(`Score : ${score} / ${rounds}`)}
            </p>
          )}
          <button type="button" className={done ? SECONDARY_BUTTON : PRIMARY_BUTTON} onClick={next}>
            {done ? 'Recommencer' : 'Question suivante'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------- outil ---------- */

export function SpaceFrameTool({
  level = null,
  seed,
}: {
  /** niveau : la formule de distance |ax₀ + by₀ + cz₀ + d| / ‖n‖ s'affiche en prépa (ou niveau inconnu) */
  level?: string | null;
  /** graine du défi (tests) */
  seed?: number;
}) {
  const rank = schoolLevelRank(level);
  const prepa = rank === null || rank >= 8;
  const [mode, setMode] = useState<'points' | 'vectors' | 'plane' | 'quiz'>('points');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Partie de l’outil"
        value={mode}
        options={[
          { value: 'points', label: 'Points' },
          { value: 'vectors', label: 'Vecteurs' },
          { value: 'plane', label: 'Droite, plan' },
          { value: 'quiz', label: 'Défi' },
        ]}
        onChange={setMode}
      />
      <div hidden={mode !== 'points'}>
        <Points />
      </div>
      <div hidden={mode !== 'vectors'}>
        <Vectors />
      </div>
      <div hidden={mode !== 'plane'}>
        <LinePlane prepa={prepa} />
      </div>
      <div hidden={mode !== 'quiz'}>
        <Challenge seed={seed} prepa={prepa} />
      </div>
    </div>
  );
}
